import { useOutletContext } from "react-router-dom";
import { useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatDate, label } from "@/lib/api";

const docConfig = [
  { kind: "identita", label: "Documento d'identità", desc: "Carta d'identità o passaporto del titolare." },
  { kind: "codice_fiscale", label: "Codice fiscale", desc: "Tessera sanitaria / certificato di attribuzione CF." },
  { kind: "visura", label: "Visura camerale", desc: "Visura ordinaria rilasciata dalla CCIAA." },
  { kind: "agreement_signed", label: "Accordo GoWeb-Rivenditore firmato", desc: "Scarichi l'accordo generato, lo firmi e lo ricarichi qui." },
];

const statusCls = { approved: "text-emerald-700", rejected: "text-rose-700", pending: "text-amber-700" };

const DocStatus = ({ doc, kind }) => {
  if (!doc) return <div className="text-xs text-slate-400 mt-2" data-testid={`doc-status-${kind}`}>Non ancora caricato</div>;
  return (
    <div className="text-xs mt-2" data-testid={`doc-status-${kind}`}>
      <span className={`font-semibold ${statusCls[doc.status] || ""}`}>{label(doc.status)}</span>
      <span className="text-slate-500"> · {doc.original_filename} · {formatDate(doc.uploaded_at)}</span>
      {doc.status === "rejected" && doc.reject_reason && <div className="text-rose-700 mt-0.5">Motivo: {doc.reject_reason}</div>}
    </div>
  );
};

export default function ResellerDocuments() {
  const me = useOutletContext();
  const api = useApi();
  const qc = useQueryClient();
  const inputs = useRef({});
  const { data: docs = [] } = useQuery({
    queryKey: ["reseller-documents"],
    queryFn: async () => (await api.get("/reseller/documents")).data,
    enabled: !!me?.reseller,
  });
  const latest = (kind) => docs.find((x) => x.kind === kind);

  const generateAgreement = useMutation({
    mutationFn: async () => (await api.post("/reseller/agreement/generate")).data,
    onSuccess: async (d) => {
      const r = await api.get(`/files/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Accordo generato e scaricato");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const upload = useMutation({
    mutationFn: async ({ kind, file }) => {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      return (await api.post("/reseller/documents", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me"] }); qc.invalidateQueries({ queryKey: ["reseller-documents"] }); toast.success("Caricato — in attesa di approvazione"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (!me?.reseller) {
    return <div className="bg-white border border-[#E2DDD5] rounded-xl p-10 text-center text-slate-500">Prima completi l'anagrafica.</div>;
  }

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Documenti e accordo</h1>
        <p className="text-sm text-slate-500 mt-1">Carichi i documenti anagrafici obbligatori e l'accordo GoWeb-Rivenditore firmato.</p>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold">Accordo GoWeb-Rivenditore</div>
            <div className="text-sm text-slate-500 mt-0.5">Generi il PDF, lo firmi in originale e lo ricarichi come "Accordo firmato".</div>
          </div>
          <Button onClick={() => generateAgreement.mutate()} className="bg-[#1E2A38] text-white hover:bg-[#0F141A]" data-testid="btn-generate-agreement">
            <Download size={14} className="mr-1" /> Genera e scarica
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {docConfig.map((d) => (
          <div key={d.kind} className="bg-white border border-[#E2DDD5] rounded-xl p-5" data-testid={`doc-${d.kind}`}>
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-[#B84A2B]" />
              <div className="font-semibold">{d.label}</div>
            </div>
            <div className="text-xs text-slate-500 mt-1">{d.desc}</div>
            <DocStatus doc={latest(d.kind)} kind={d.kind} />
            <input ref={(el) => (inputs.current[d.kind] = el)} type="file" accept="application/pdf,image/*" className="hidden"
                   onChange={(e) => e.target.files?.[0] && upload.mutate({ kind: d.kind, file: e.target.files[0] })} data-testid={`file-${d.kind}`} />
            <Button variant="outline" size="sm" className="mt-3 border-[#E2DDD5]" onClick={() => inputs.current[d.kind]?.click()} data-testid={`btn-upload-${d.kind}`}><UploadCloud size={14} className="mr-1" /> Carica file</Button>
          </div>
        ))}
      </div>
    </div>
  );
}
